import settle from 'axios/unsafe/core/settle.js';
import AxiosError from 'axios/unsafe/core/AxiosError.js';
import CanceledError from 'axios/unsafe/cancel/CanceledError.js';
import parseProtocol from 'axios/unsafe/helpers/parseProtocol.js';
import platform from 'axios/unsafe/platform/index.js';
import AxiosHeaders from 'axios/unsafe/core/AxiosHeaders.js';
import {progressEventReducer} from 'axios/unsafe/helpers/progressEventReducer.js';
import resolveConfig from "axios/unsafe/helpers/resolveConfig.js";
import {flutterRequest} from "@hybird/flutter/bridge/request";

const isXHRAdapterSupported = typeof XMLHttpRequest !== 'undefined';

function formDataToMap(formData) {
  if (formData instanceof FormData) {
    const map = {};
    for (const [key, value] of formData.entries()) {
      map[key] = value;
    }
    return map;
  } else {
    return formData;
  }
}

export default isXHRAdapterSupported && function (config) {
  return new Promise(function dispatchXhrRequest(resolve, reject) {
    const _config = resolveConfig(config);
    let requestData = formDataToMap(_config.data);
    const requestHeaders = AxiosHeaders.from(_config.headers).normalize();
    let {responseType, onUploadProgress, onDownloadProgress} = _config;
    let onCanceled;
    let uploadThrottled, downloadThrottled;
    let flushUpload, flushDownload;

    function done() {
      flushUpload && flushUpload(); // flush events
      flushDownload && flushDownload(); // flush events

      _config.cancelToken && _config.cancelToken.unsubscribe(onCanceled);

      _config.signal && _config.signal.removeEventListener('abort', onCanceled);
    }

    let onSendProgress;
    let onReceiveProgress;
    if (onUploadProgress) {
      ([uploadThrottled, flushUpload] = progressEventReducer(onUploadProgress));
      onSendProgress = data => uploadThrottled(data);
    }

    if (onDownloadProgress) {
      ([downloadThrottled, flushDownload] = progressEventReducer(onDownloadProgress, true));
      onReceiveProgress = data => downloadThrottled(data);
    }
    const request = {
      basePath: _config.download?.basePath,
      savePath: _config.download?.savePath,
      url: _config.url,
      data: requestData,
      headers: requestHeaders,
      method: _config.method.toUpperCase(),
      sendTimeout: _config.timeout,
      receiveTimeout: _config.timeout,
      withCredentials: !!_config.withCredentials
    };

    flutterRequest.request(request, {
      onSendProgress,
      onReceiveProgress,
      onFinish: (data) => {
        const response = {
          data: data.data,
          status: data.statusCode,
          statusText: data.statusMessage,
          headers: AxiosHeaders.from(data.headers),
          config,
          request
        };

        settle(function _resolve(value) {
          resolve(value);
          done();
        }, function _reject(err) {
          reject(err);
          done();
        }, response);
      },
      onError: data => {
        reject(new AxiosError(data.message, AxiosError.ERR_NETWORK, config, request));
      }
    }).then(data => {
      if (_config.cancelToken || _config.signal) {
        // Handle cancellation
        // eslint-disable-next-line func-names
        onCanceled = cancel => {
          data.method?.abort();
          reject(!cancel || cancel.type ? new CanceledError(null, config, xda) : cancel);
        };

        _config.cancelToken && _config.cancelToken.subscribe(onCanceled);
        if (_config.signal) {
          _config.signal.aborted ? onCanceled() : _config.signal.addEventListener('abort', onCanceled);
        }
      }
    });

    const protocol = parseProtocol(_config.url);

    if (protocol && platform.protocols.indexOf(protocol) === -1) {
      reject(new AxiosError('Unsupported protocol ' + protocol + ':', AxiosError.ERR_BAD_REQUEST, config));
    }
  });
}
